import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'destructive';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'> {
  variant?: Variant;
  children: ReactNode;
  /**
   * Disabled buttons require a stated reason (spec §6). A greyed control with no
   * explanation is named in the spec as the single biggest support cost in
   * volunteer software, so the type makes the reason non-optional.
   */
  disabledReason?: string;
  fullWidth?: boolean;
}

/**
 * Everything a button looks like except which variant it is.
 *
 * Exported because a `mailto:` is a navigation and belongs on an `<a>`, which this component
 * cannot render — so `BetaNotice` needs the same geometry on a different element. It shares the
 * string rather than retyping it: the first draft copied it and had already lost the transition
 * classes, which is the drift that arrives silently and that nothing type-checks. A second element
 * wearing this must also carry one of the `th-btn--*` variants below.
 */
export const BUTTON_BASE =
  'th-btn inline-flex items-center justify-center gap-2 rounded-md t-body-strong ' +
  'min-h-[48px] px-5 transition-[background-color,transform] duration-[120ms] ' +
  'motion-reduce:transition-none select-none';

/**
 * 48px min height, radius-md, label a verb phrase in sentence case — never
 * all-caps (spec §6).
 *
 * Ember never fills a button. The only ember-filled control in the product is
 * the thread send button, which is built in Composer rather than here so this
 * component cannot be used to introduce a second one.
 */
export function Button({
  variant = 'primary',
  children,
  disabledReason,
  fullWidth,
  className = '',
  ...rest
}: ButtonProps) {
  const isDisabled = Boolean(disabledReason);

  const base = BUTTON_BASE;

  const variants: Record<Variant, string> = {
    primary: 'th-btn--primary',
    secondary: 'th-btn--secondary',
    destructive: 'th-btn--destructive',
  };

  return (
    <span className={fullWidth ? 'flex flex-col gap-1' : 'inline-flex flex-col gap-1'}>
      <button
        type="button"
        {...rest}
        className={`${base} ${isDisabled ? 'th-btn--disabled' : variants[variant]} ${
          fullWidth ? 'w-full' : ''
        } ${className}`}
        aria-disabled={isDisabled || undefined}
        // aria-disabled rather than the disabled attribute: a disabled element is
        // removed from the tab order, so a screen-reader user can never reach it to
        // discover *why* it is unavailable.
        onClick={isDisabled ? (e) => e.preventDefault() : rest.onClick}
      >
        {children}
      </button>

      {disabledReason && (
        <span className="t-caption" style={{ color: 'var(--color-text-muted)' }}>
          {disabledReason}
        </span>
      )}
    </span>
  );
}
